import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import Flex from '../../../components/Flex';
import { Button, Dropdown, Input } from '@prio365/prio365-react-library';
import { DropdownOption } from '@prio365/prio365-react-library/lib/Dropdown/components/Dropdown';
import { v4 as uuid } from 'uuid';
import { DashboardItem } from '../../../models/Dashboard';

const useStyles = makePrioStyles((theme) => ({
  root: {},
}));

export interface LinkComponent {
  id: string;
  title: string;
  url: string;
  description: string;
}

interface DashboardLinkData {
  title: string;
  links: LinkComponent[];
}

export interface DashboardEditWidgetLinkContentDrawerHandle {
  getData: () => { title: string; links: LinkComponent[] };
}

interface DashboardEditWidgetLinkContentDrawerProps {
  className?: string;
  dashboardItem: DashboardItem;
  onValidityChange?: (isValid: boolean) => void;
}

export const DashboardEditWidgetLinkContentDrawer = forwardRef<
  DashboardEditWidgetLinkContentDrawerHandle,
  DashboardEditWidgetLinkContentDrawerProps
>((props, ref) => {
  //#region ------------------------------ Defaults
  const { className, dashboardItem, onValidityChange } = props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors

  const emptyLink: LinkComponent = {
    id: uuid(),
    title: '',
    url: '',
    description: '',
  };
  const initialData: DashboardLinkData =
    typeof dashboardItem?.data === 'object' && dashboardItem.data?.links
      ? dashboardItem.data
      : { title: t('dashboard:links.title'), links: [emptyLink] };

  const [links, setLinks] = useState<LinkComponent[]>(
    initialData.links.length > 0 ? initialData.links : [emptyLink]
  );
  const [title, setTitle] = useState(initialData.title);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const updateLink = (updatedLink: LinkComponent) => {
    setLinks((prev) =>
      prev.map((link) => (link.id === updatedLink.id ? updatedLink : link))
    );
  };

  const moveLink = (
    links: LinkComponent[],
    currentLink: LinkComponent,
    direction: 'up' | 'down'
  ): LinkComponent[] => {
    const index = links.findIndex((link) => link.id === currentLink.id);
    if (index === -1) return links; //

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= links.length) return links;

    const updatedLinks = [...links];
    const [selectedLink] = updatedLinks.splice(index, 1);
    updatedLinks.splice(newIndex, 0, selectedLink);

    return updatedLinks;
  };
  //#endregion

  //#region ------------------------------ Effects

  useEffect(() => {
    if (onValidityChange) {
      const isValid = links.every(
        (link) => !!link.title.trim() && !!link.url.trim()
      );
      onValidityChange(isValid);
    }
  }, [links, onValidityChange]);

  useImperativeHandle(
    ref,
    () => ({
      getData: () => ({ title, links }),
    }),
    [title, links]
  );
  //#endregion

  //#region ------------------------------ Component
  const LinkElement = (link: LinkComponent, index: number) => (
    <Flex.Column
      childrenGap={theme.spacing.regular}
      backgroundColor={theme.colors.application.background.light}
      padding={8}
    >
      <Input
        full
        label={t('dashboard:widgets.editWidgetItemDrawer.link.title')}
        value={link.title}
        onChangeWithoutDebounce={(value) => {
          updateLink({
            ...link,
            title: value,
          });
        }}
      ></Input>
      <Input
        full
        label={t('dashboard:widgets.editWidgetItemDrawer.link.url')}
        value={link.url}
        onChangeWithoutDebounce={(value) => {
          updateLink({
            ...link,
            url: value,
          });
        }}
      ></Input>
      <Input
        full
        label={t('dashboard:widgets.editWidgetItemDrawer.link.description')}
        value={link.description || ''}
        onChangeWithoutDebounce={(value) => {
          updateLink({
            ...link,
            description: value,
          });
        }}
      ></Input>
      <Flex.Row justifyContent="flex-end" childrenGap={theme.spacing.small}>
        {links.length > 1 && (
          <Dropdown
            placement="bottomLeft"
            options={
              [
                ...(index > 0
                  ? [
                      {
                        label: t(
                          'dashboard:widgets.editWidgetItemDrawer.link.actions.moveUp'
                        ),
                        onClick: () =>
                          setLinks((prev) => moveLink(prev, link, 'up')),
                        value: 'move-up',
                      },
                    ]
                  : []),
                ...(index < links.length - 1
                  ? [
                      {
                        label: t(
                          'dashboard:widgets.editWidgetItemDrawer.link.actions.moveDown'
                        ),
                        onClick: () =>
                          setLinks((prev) => moveLink(prev, link, 'down')),
                        value: 'move-down',
                      },
                    ]
                  : []),
              ] as DropdownOption[]
            }
          >
            <Button iconProp={['fal', 'ellipsis-v']} type="default" />
          </Dropdown>
        )}
        <Button
          type="danger"
          style={{ width: 'fit-content' }}
          onClick={() =>
            setLinks((prev) => prev.filter((l) => l.id !== link.id))
          }
        >
          {t('dashboard:widgets.editWidgetItemDrawer.link.actions.delete')}
        </Button>
      </Flex.Row>
    </Flex.Column>
  );
  //#endregion

  return (
    <Flex.Column
      className={classNames(className, classes.root)}
      childrenGap={theme.spacing.regular}
    >
      <Input
        full
        label={t('dashboard:widgets.editWidgetItemDrawer.widgetTitle')}
        value={title}
        onChangeWithoutDebounce={setTitle}
      />
      {links.map((link, index) => LinkElement(link, index))}
      <Button
        type="link"
        onClick={() => setLinks((prev) => [...prev, emptyLink])}
        disabled={links.some((link) => !link.title.trim() || !link.url.trim())}
      >
        {t('dashboard:widgets.editWidgetItemDrawer.addLink')}
      </Button>
    </Flex.Column>
  );
});

export default DashboardEditWidgetLinkContentDrawer;
